import { makeAutoObservable, runInAction } from "mobx";
import RestaurantService from "../services/RestaurantService";
import { DeliveryInfo } from '../types/entities';
import DeliveryService from '../services/DeliveryService';

export default class RestaurantStore {
  // EN: Default to false and loading to true. The UI will show a loading state instead of an ambiguous disabled state.
  // PL: Domyślnie false i ładowanie na true. Interfejs użytkownika pokaże stan ładowania zamiast niejednoznacznego stanu wyłączonego.
  isDeliveryEnabled: boolean = false;
  deliveryInfo: DeliveryInfo | null = null; // EN: Start with loading false. The first check will set it to true. This is cleaner than starting with true and bypassing the loading guard.
  // PL: Zacznij z ładowaniem ustawionym na false. Pierwsze sprawdzenie ustawi go na true. To czystsze rozwiązanie niż zaczynanie z true i omijanie warunku sprawdzającego ładowanie.
  isLoading = false;
  error: string | null = null;

  private pollingInterval = 5 * 60 * 1000; // 5 minutes in milliseconds
  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private initialized = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // --- lifecycle ---
  async initialize() {
    if (this.initialized) return;
    this.initialized = true;

    await this.checkDeliveryStatus();
    this.startPolling();
  }

  destroy() {
    this.stopPolling();
    this.initialized = false;
  }
  
  // --- Actions ---
  setIsDeliveryEnabled(status: boolean) {
    this.isDeliveryEnabled = status;
  }

  setLoading(status: boolean) {
    this.isLoading = status;
  }
  
  setError(error: string | null) {
    this.error = error;
  }

  setDeliveryInfo(info: DeliveryInfo | null) {
    this.deliveryInfo = info;
}


  // --- Async Actions ---
  startPolling() {
    // EN: Clear any existing timer to prevent duplicates if called multiple times.
    // PL: Wyczyść istniejący timer, aby zapobiec duplikatom w przypadku wielokrotnego wywołania.
    this.stopPolling();

    // EN: Schedule the next poll after the current one has finished.
    // PL: Zaplanuj następne odpytywanie po zakończeniu bieżącego.
    this.pollingTimer = setTimeout( async () => {
      await this.checkDeliveryStatus();
      this.startPolling(); // Schedule the next poll after this one completes.
    }, this.pollingInterval);
  };

  stopPolling() {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  };

    // --- core logic ---
  async checkDeliveryStatus() {
    this.setLoading(true);
    this.setError(null);

    try {
      const data = await RestaurantService.getDeliveryStatus();

      if(data) {
        runInAction(() => {
          const actualData = Array.isArray(data) ? data[0] : data;
          
          const isEnabled = 
            actualData.isEnabled ??
            actualData.isDeliveryEnabled ?? 
            actualData.is_delivery_enabled ?? 
            false;

          this.setIsDeliveryEnabled(isEnabled);
        });
      }
    } catch (e: any) {
      const status = e?.response?.status;

      let message =
        e.response?.data?.message || 
        "Failed to fetch delivery status";
      // EN: Provide a more specific hint for 403 errors, which often indicate a server configuration issue.
      // PL: Podaj bardziej szczegółową wskazówkę dla błędów 403, które często wskazują na problem z konfiguracją serwera.
      if (status === 403) {
        message = "403 Forbidden: delivery status access denied";
      }

      runInAction(() => {
        this.setError(message);
        // EN: As a safe default, disable delivery if status check fails.
        // PL: Jako bezpieczne ustawienie domyślne, wyłącz dostawę, jeśli sprawdzanie statusu nie powiodło się.
        this.setIsDeliveryEnabled(false);
      });
    } finally {
      runInAction(() => {
        this.setLoading(false)
      });
    }
  }

  async updateDeliveryStatus(isEnabled: boolean) {
    this.setLoading(true);
    this.setError(null);

    try {
      await RestaurantService.updateDeliveryStatus(isEnabled);

      runInAction(() => 
        this.setIsDeliveryEnabled(isEnabled)
    );
    } catch (e: any) {
      const message =
      e.response?.data?.message ?? 
      "Update failed";

      runInAction(() => {
        this.setError(message)
      });

      throw e; // EN: Re-throw to handle in component. / PL: Rzuć ponownie, aby obsłużyć w komponencie.
    } finally {
      runInAction(() => {
        this.setLoading(false)
      });
    }
  }
}

